/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps } from 'preact';
import { getClientHints } from '../utils/PRIVATE_clientHints';

import { Menu } from './Menu';

/**
 * Returns the key from the item element
 */
export const getKey = (item: HTMLElement) => {
  // Key is set on data-oj-menu-item-key on each menuItem. To get this key from the
  // the actual menuItem we need to do a kebab to camel-case conversion.
  return item.dataset['ojMenuItemKey'];
};

/**
 * A helper function that return the key of the n available item.
 */
export const getNVisible = (root: HTMLElement | null, positionIndex: number) => {
  if (root) {
    const items = root.querySelectorAll('[data-oj-menu-item-key]');
    const positionedItem = items[positionIndex] as HTMLElement;
    if (positionedItem) {
      const key = getKey(positionedItem as HTMLElement);
      if (key) {
        return key;
      }
    }
  }
  return null;
};

/**
 * A helper function that return the key of the last available item.
 */
export const getLastVisible = (root: HTMLElement | null) => {
  if (root) {
    const items = root.querySelectorAll('[data-oj-menu-item-key]');
    const lastItem = items[items.length - 1] as HTMLElement;
    if (lastItem) {
      const key = getKey(lastItem as HTMLElement);
      if (key) {
        return key;
      }
    }
  }
  return null;
};

/**
 * A helper function that returns the key of the previous or the next item
 * given the specified current key
 */
export const getPrevNext = (root: HTMLElement | null, isPrev: boolean, currentKey?: string) => {
  return (): string | null => {
    if (root) {
      const items = Array.from(root.querySelectorAll('[data-oj-menu-item-key]')) as HTMLElement[];
      const currentKeyIndex = items.findIndex((item) => getKey(item) === currentKey);
      let nextIndex = currentKeyIndex + (isPrev ? -1 : 1);
      const itemsLastIndex = items.length - 1;
      if (nextIndex < 0) {
        nextIndex = itemsLastIndex;
      }
      if (nextIndex > itemsLastIndex) {
        nextIndex = 0;
      }

      const key = getKey(items[nextIndex]);
      return key ? key : null;
    }
    return null;
  };
};

/**
 * A helper function to get the key from an element
 */
export const keyElemExtractor = (
  element: HTMLElement
): { key: string; elem: HTMLElement } | null => {
  const elem = element.closest('[data-oj-menu-item-key]') as HTMLElement;
  if (elem) {
    const key = getKey(elem as HTMLElement);
    return key === undefined ? null : ({ key, elem } as { key: string; elem: HTMLElement });
  }
  return null;
};

/**
 * Function to get the corresponding keyboard behavior to close the menu.
 */
export const getKeyboardCloseProps = (
  onClose: ComponentProps<typeof Menu>['onClose'],
  menuLevel: 'main' | 'sub'
) => {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.stopPropagation();
      onClose?.({ reason: 'dismissed' });
      event.preventDefault(); //TODO: Revisit this since we should let focus be at its natural flow, quick solution for now to focus correct item in main use case
    } else if (event.key === 'Escape' && menuLevel === 'main') {
      event.stopPropagation();
      onClose?.({ reason: 'dismissed' });
    }
  };
  return { onKeyDown };
};

/**
 * Function to get the corresponding specific behavior for closing submenu via keyboard
 */
export const getCloseSubmenuEvent = (direction: 'ltr' | 'rtl', closeSubmenu?: () => void) => {
  if (!closeSubmenu) {
    return {};
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (
      e.key === 'Escape' ||
      (direction === 'ltr' && e.key === 'ArrowLeft') ||
      (direction === 'rtl' && e.key === 'ArrowRight')
    ) {
      e.stopPropagation();
      closeSubmenu?.();
    }
  };
  return { onKeyDown };
};

/**
 * Function to get the corresponding specific behavior for opening submenu via keyboard
 */
export const getOpenSubmenuEvent = (direction: 'ltr' | 'rtl', openSubmenu?: () => void) => {
  if (!openSubmenu) {
    return {};
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (direction === 'ltr') {
      if (e.key === 'ArrowRight') {
        openSubmenu?.();
      }
    } else {
      if (e.key === 'ArrowLeft') {
        openSubmenu?.();
      }
    }
  };
  return { onKeyDown };
};

/**
 * Functions to get type of device
 */
export const getIsMobile = () => {
  const { deviceRenderMode } = getClientHints();
  return deviceRenderMode === 'phone';
};

export const getIsIosTablet = () => {
  const { deviceRenderMode, platform } = getClientHints();
  return platform === 'ios' && deviceRenderMode === 'tablet';
};

/**
 * Get blur and focus events
 */
export const getMenuContainerFocusMethods = (
  blurFunction: () => void,
  focusFunction: () => void
) => {
  const onBlur = () => {
    setTimeout(() => {
      blurFunction();
    }, 100);
  };

  const onFocus = () => {
    focusFunction();
  };

  return { onBlur, onFocus };
};

/**
 * Set currentKey based on clicking menuItem
 */
export const getMenuItemClickChangeKey = (
  key: string,
  changeKeyMethod: (payload: { key: string; reason: 'click' | 'mouseEnter' | 'keyboard' }) => void
) => {
  const onClick = () => {
    changeKeyMethod({ key, reason: 'click' });
  };
  return { onClick };
};

export const getMenuContainerMouseLeave = (mouseLeaveFunction: (e: MouseEvent) => void) => {
  const onMouseLeave = (e: MouseEvent) => {
    mouseLeaveFunction(e);
  };
  return { onMouseLeave };
};

/**
 * Function to get the corresponding specific behavior for clicking/touching down menuContainer
 */
export const getMenuPointerDown = () => {
  const onPointerDown = (event: PointerEvent) => {
    //We just want to prevent default when element that was
    //pressed down was not a menu item.
    const keyElem = keyElemExtractor(event.target as HTMLElement);
    if (!keyElem?.key) {
      event.preventDefault();
    }
  };
  return { onPointerDown };
};

export const isMenuChildrenFalsy = (children: ComponentChildren) => {
  return !children || (Array.isArray(children) && !children.length);
};

export type MenuValueUpdateDetail<T> = {
  previousValue?: T;
  value: T;
};

export type MenuCollectionItem = {
  key?: string;
  reason: 'mouseEnter' | 'click' | 'keyboard' | 'touch';
};
